/**
 * Cookie/privacy preferences entry point.
 *
 * Tries the external CMP (consentmanager.net) first. If the CMP script never
 * loaded (blocked, offline, account paused), it falls back to the built-in
 * consent banner so users always have a working way to change preferences.
 */

type CmpFn = (command: string, ...args: unknown[]) => void;

interface CmpWindow extends Window {
  __cmp?: CmpFn;
  __tcfapi?: CmpFn;
  __ENABLE_EXTERNAL_CMP?: boolean;
  __CMP_UNAVAILABLE?: boolean;
}

export const OPEN_COOKIE_PREFERENCES_EVENT = 'open_cookie_preferences';
export const CMP_UNAVAILABLE_EVENT = 'cmp_unavailable';

export const isExternalCmpReady = (): boolean => {
  if (typeof window === 'undefined') return false;
  const w = window as CmpWindow;
  return !w.__CMP_UNAVAILABLE && typeof w.__cmp === 'function';
};

/** Opens the privacy settings UI. Returns true when the external CMP handled it. */
export const openCookiePreferences = (): boolean => {
  if (typeof window === 'undefined') return false;
  const w = window as CmpWindow;

  if (isExternalCmpReady()) {
    try {
      w.__cmp?.('displayConsentUi', 1);
      return true;
    } catch {
      // fall through to the built-in banner
    }
  }

  w.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFERENCES_EVENT));
  return false;
};

interface CmpData {
  consentExists?: boolean;
  userChoiceExists?: boolean;
  lastButtonEvent?: number;
  purposeConsents?: Record<string, boolean>;
  purposesList?: Array<{ id: string; name: string }>;
}

const purposeGranted = (d: CmpData, name: string): boolean => {
  const id = d.purposesList?.find((p) => p.name.toLowerCase() === name)?.id;
  return !!id && d.purposeConsents?.[id] === true;
};

/**
 * Mirrors the consentmanager.net decision into the site's own consent record
 * so gated trackers (Meta Pixel) follow what the visitor chose in that banner.
 * Marketing = the CMP "Marketing" purpose; analytics = "Measurement".
 */
export const syncExternalCmpConsent = (onChange: (marketing: boolean, analytics: boolean) => void): void => {
  if (typeof window === 'undefined') return;
  const w = window as CmpWindow;
  let attempts = 0;

  const apply = () => {
    let d: CmpData | undefined;
    try {
      d = (w.__cmp as unknown as (c: string) => CmpData)('getCMPData');
    } catch {
      return;
    }
    if (!d || !d.userChoiceExists) return;
    // The CMP keeps purposes flagged even after "Reject all" (button event 2),
    // so an explicit rejection always wins.
    const rejected = d.lastButtonEvent === 2;
    const marketing = !rejected && purposeGranted(d, 'marketing');
    const analytics = !rejected && purposeGranted(d, 'measurement');
    const next = JSON.stringify({ necessary: true, analytics, marketing });
    if (localStorage.getItem('cookie_consent') === next && localStorage.getItem('cookie_consent_date')) return;
    localStorage.setItem('cookie_consent', next);
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    onChange(marketing, analytics);
  };

  const attach = () => {
    if (typeof w.__cmp !== 'function') {
      if (++attempts < 40 && !w.__CMP_UNAVAILABLE) window.setTimeout(attach, 250);
      return;
    }
    try {
      w.__cmp('addEventListener', ['consent', () => apply(), false], null);
    } catch {
      /* ignore */
    }
    apply();
  };
  attach();
};
